if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: ['.product__media-item'],
        };

        this.sliderInstance = false;
        this.thumbsInstance = false;
        this.variantFeaturedMediaMap = {};
        this.variantChangeUnsubscriber = undefined;
        this.handleVariantChangedEvent = this.handleVariantChangedEvent.bind(this);
      }

      connectedCallback() {
        this.parseVariantFeaturedMediaMap();
        this.variantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
          this.handleVariantChange.bind(this)
        );
        document.addEventListener('variant:changed', this.handleVariantChangedEvent);
        FoxTheme.Motion.inView(this, this.init.bind(this));
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
        document.removeEventListener('variant:changed', this.handleVariantChangedEvent);
      }

      parseVariantFeaturedMediaMap() {
        const mapElement = this.querySelector('[data-variant-featured-media]');
        if (!mapElement) return;

        try {
          this.variantFeaturedMediaMap = JSON.parse(mapElement.textContent);
        } catch (error) {
          this.variantFeaturedMediaMap = {};
        }
      }

      get sectionId() {
        return this.id.replace('MediaGallery-', '');
      }

      handleVariantChange({ data }) {
        if (!data?.variant || data.sectionId !== this.sectionId) return;
        this.setActiveMedia(data.variant);
      }

      handleVariantChangedEvent(event) {
        const variant = event.detail?.variant;
        if (!variant) return;
        this.setActiveMedia(variant);
      }

      resolveFeaturedMedia(variant) {
        if (!variant) return null;

        const mappedMedia =
          this.variantFeaturedMediaMap?.[variant.id] || this.variantFeaturedMediaMap?.[String(variant.id)];
        if (mappedMedia) return mappedMedia;

        if (variant.featured_media?.preview_image?.src) return variant.featured_media;

        return null;
      }

      init() {
        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        this.setSliderOptions();

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();

        if (this.enableImageZoom) {
          const allMedia = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
          this.initImageZoom(allMedia);
        }

        this.syncVariantFeaturedMedia();
      }

      syncVariantFeaturedMedia() {
        const productInfo = this.closest('product-info');
        const variantScript = productInfo?.querySelector('variant-selects [data-selected-variant]');
        if (!variantScript?.textContent) return;

        try {
          const variant = JSON.parse(variantScript.textContent);
          this.setActiveMedia(variant);
        } catch (error) {
          console.warn('Could not parse selected variant for media gallery.', error);
        }
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);

        this.sliderOptions = {
          init: false,
          slidesPerView: '1',
          spaceBetween: mediaItemGap,
          loop: false,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'fraction',
          },
          threshold: 2,
        };

        this.thumbsOptions = {
          slidesPerView: 4,
          breakpoints: {
            461: {
              slidesPerView: 5,
            },
          },
          spaceBetween: mediaItemGap,
          loop: false,
          freeMode: true,
          watchSlidesProgress: true,
          threshold: 2,
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions = Object.assign({}, this.thumbsOptions, {
              breakpoints: {
                768: {
                  direction: 'vertical',
                  slidesPerView: 'auto',
                },
              },
            });
            break;
          case 'slider-freemode':
            this.sliderOptions = Object.assign({}, this.sliderOptions, {
              slidesPerView: 'auto',
            });
            break;
        }
      }

      updateMediaLayout() {
        if (FoxTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions);
            this.thumbsInstance.init();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 2,
            };
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs,
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();
        }
      }

      destroySlider() {
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy();
          this.sliderInstance = false;
        }
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== 'object') {
          this.thumbsInstance = new window.FoxTheme.Carousel(this.selectors.thumbnails, this.thumbsOptions);
          this.thumbsInstance.init();
        }
      }

      initImageZoom(items = []) {
        let dataSource = [];
        if (items.length > 0) {
          items.forEach((media) => {
            switch (media.dataset.mediaType) {
              case 'model':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('product-model').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'video':
              case 'external_video':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('video-element').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'image':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  src: media.dataset.src,
                  width: media.dataset.pswpWidth,
                  height: media.dataset.pswpHeight,
                  mediaType: media.dataset.mediaType,
                });
                break;
            }
          });
        }

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
          preload: [1, 4],
        });

        this.lightbox.addFilter('thumbEl', (thumbEl, { id }, index) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el;
              }
            }
          }

          return thumbEl;
        });

        this.lightbox.addFilter('placeholderSrc', (placeholderSrc, { data }) => {
          if (data.src) {
            return data.src;
          }
          return placeholderSrc;
        });

        this.lightbox.on('change', () => {
          window.pauseAllMedia(this);
        });

        this.lightbox.on('destroy', () => {
          const { currIndex } = this.lightbox.pswp;

          if (this.sliderInstance && this.sliderInstance.slider) {
            this.sliderInstance.slider.slideToLoop(currIndex, 0, false);
          }
        });

        this.lightbox.on('pointerDown', (e) => {
          if (this.lightbox.pswp.currSlide.data.mediaType != 'image') {
            e.preventDefault();
          }
        });

        this.lightbox.on('uiRegister', () => {
          if (!this.onlyImage) {
            this.lightbox.pswp.ui.registerElement({
              name: 'next',
              ariaLabel: 'Next slide',
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.next();
              },
            });
            this.lightbox.pswp.ui.registerElement({
              name: 'prev',
              ariaLabel: 'Previous slide',
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.prev();
              },
            });
          }
          this.lightbox.pswp.ui.registerElement({
            name: 'close-zoom',
            ariaLabel: 'Close zoom image',
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, el) => {
              this.lightbox.pswp.close();
            },
          });
        });

        this.lightbox.init();

        FoxTheme.utils.addEventDelegate({
          selector: '.js-photoswipe--zoom',
          context: this,
          handler: (e, media) => {
            if (media.dataset.mediaType === 'image') {
              const index = Number(media.dataset.mediaIndex) || 0;
              this.lightbox.loadAndOpen(index);
            }
          },
        });
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;

          if (slides[activeIndex]) {
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      handleSlideChange() {
        this.sliderInstance.slider.on('realIndexChange', (swiper) => {
          const { slides, activeIndex, thumbs } = swiper;

          if (thumbs.swiper) {
            thumbs.swiper.slideTo(activeIndex);
          }

          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);

            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      setActiveMedia(variant) {
        const featuredMedia = this.resolveFeaturedMedia(variant);
        const featuredSlide = this.querySelector('[data-variant-featured-slide]');

        if (featuredMedia && featuredSlide) {
          this.updateVariantFeaturedSlide(featuredSlide, featuredMedia);
          const featuredThumb = this.querySelector('[data-variant-featured-thumb]');
          if (featuredThumb) {
            this.updateVariantFeaturedThumb(featuredThumb, featuredMedia);
          }
        }

        if (this.sliderInstance?.slider) {
          this.sliderInstance.slider.slideTo(0, 0, false);
          if (this.thumbsInstance?.slider) {
            this.thumbsInstance.slider.slideTo(0, 0, false);
          }
        } else if (featuredSlide) {
          if (!FoxTheme.config.mqlMobile) {
            window.scrollTo({ top: featuredSlide.offsetTop, behavior: 'smooth' });
          }
        }
      }

      updateVariantFeaturedSlide(slide, featuredMedia) {
        const preview = featuredMedia.preview_image;
        if (!preview?.src) return;

        const imageSizes = this.dataset.imageSizes;

        slide.dataset.mediaId = featuredMedia.id;
        slide.dataset.mediaType = featuredMedia.media_type || 'image';
        slide.dataset.src = preview.zoom_src || preview.src;
        slide.dataset.pswpWidth = preview.width;
        slide.dataset.pswpHeight = Math.ceil(preview.width / (preview.aspect_ratio || 1));

        const container = slide.querySelector('.product__media-container');
        if (container) {
          container.style.setProperty('--aspect-ratio', preview.aspect_ratio || 1);
          container.classList.remove('media-type-image', 'media-type-video', 'media-type-external_video', 'media-type-model');
          container.classList.add(`media-type-${featuredMedia.media_type || 'image'}`);
        }

        const img = slide.querySelector('img');
        if (img) {
          if (preview.srcset) {
            img.srcset = preview.srcset;
          } else {
            img.removeAttribute('srcset');
          }

          if (imageSizes) {
            img.sizes = imageSizes;
          } else {
            img.removeAttribute('sizes');
          }

          img.src = preview.src;
          img.alt = featuredMedia.alt || '';
          if (preview.width) {
            img.width = preview.width;
            img.height = preview.height;
          }
        }

        const zoomButton = slide.querySelector('.js-photoswipe--zoom');
        if (zoomButton) {
          zoomButton.dataset.mediaId = featuredMedia.id;
        }

        if (this.enableImageZoom) {
          const items = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
          this.initImageZoom(items);
        }
      }

      updateVariantFeaturedThumb(thumb, featuredMedia) {
        const preview = featuredMedia.preview_image;
        if (!preview?.src) return;

        const thumbSizes = '(max-width: 767px) calc((100vw - 30px) / 4), (max-width: 1023px) 70px, 150px';

        thumb.dataset.target = featuredMedia.id;

        const img = thumb.querySelector('img');
        if (img) {
          if (preview.thumb_srcset) {
            img.srcset = preview.thumb_srcset;
          } else {
            img.removeAttribute('srcset');
          }

          img.sizes = thumbSizes;
          img.src = preview.thumb_src || preview.src;
          img.alt = featuredMedia.alt || '';
        }
      }
    }
  );
}
